import React, { useState, useEffect } from 'react';
import { useUser } from '../../context/UserContext';
import { jobsAPI } from '../../services/api';
import '../admin/AdminComponents.css';

const ApplicationsManagement = () => {
  const { hasPermission } = useUser();
  const canCreateApplications = hasPermission('applications.create');
  const canEditApplications = hasPermission('applications.edit');
  const canArchiveApplications = hasPermission('applications.archive');
  const canDeleteApplications = hasPermission('applications.delete');
  const canManageStatus = canEditApplications || canArchiveApplications;
  const [applications, setApplications] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedJob, setSelectedJob] = useState('');
  const [selectedStatus, setSelectedStatus] = useState('');
  const [selectedApplication, setSelectedApplication] = useState(null);
  const [statusUpdate, setStatusUpdate] = useState({ status: '', notes: '' });
  const [showStatusModal, setShowStatusModal] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [createForm, setCreateForm] = useState({
    positionId: '',
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    coverLetter: '',
    notes: '',
    oauthUserId: '',
    characterId: ''
  });

  useEffect(() => {
    loadJobs();
    loadApplications();
  }, [selectedJob, selectedStatus]);

  const loadJobs = async () => {
    if (!hasPermission('jobs.view')) {
      setJobs([]);
      return;
    }
    try {
      const response = await jobsAPI.getAll(true);
      if (response.success) {
        setJobs(response.data);
      }
    } catch (error) {
      console.error('Error loading jobs:', error);
    }
  };

  const loadApplications = async () => {
    try {
      setLoading(true);
      const response = await jobsAPI.getApplications(
        selectedJob || null,
        selectedStatus || null
      );
      if (response.success) {
        setApplications(response.data);
      }
    } catch (error) {
      console.error('Error loading applications:', error);
      alert(error.message || 'Failed to load applications');
    } finally {
      setLoading(false);
    }
  };

  const handleStatusClick = (application) => {
    if (!canManageStatus) {
      return;
    }
    setSelectedApplication(application);
    setStatusUpdate({
      status: application.status,
      notes: application.notes || ''
    });
    setShowStatusModal(true);
  };

  const handleStatusUpdate = async () => {
    if (!statusUpdate.status) {
      alert('Please select a status');
      return;
    }

    if (statusUpdate.status === 'archived' && !canArchiveApplications) {
      alert('You do not have permission to archive applications.');
      return;
    }

    if (statusUpdate.status !== 'archived' && !canEditApplications) {
      alert('You do not have permission to edit applications.');
      return;
    }

    try {
      await jobsAPI.updateApplicationStatus(
        selectedApplication.id,
        statusUpdate.status,
        statusUpdate.notes
      );
      setShowStatusModal(false);
      setSelectedApplication(null);
      loadApplications();
    } catch (error) {
      alert(error.message || 'Failed to update status');
    }
  };

  const handleCreateApplication = async () => {
    if (!createForm.positionId || !createForm.firstName.trim() || !createForm.lastName.trim() || !createForm.email.trim()) {
      alert('Position, first name, last name, and ((Discord)) are required.');
      return;
    }

    try {
      const response = await jobsAPI.createApplicationAdmin({
        positionId: parseInt(createForm.positionId, 10),
        firstName: createForm.firstName,
        lastName: createForm.lastName,
        email: createForm.email,
        phone: createForm.phone,
        coverLetter: createForm.coverLetter,
        notes: createForm.notes,
        oauthUserId: createForm.oauthUserId ? parseInt(createForm.oauthUserId, 10) : null,
        characterId: createForm.characterId ? parseInt(createForm.characterId, 10) : null
      });

      if (response.success) {
        setShowCreateModal(false);
        setCreateForm({
          positionId: '',
          firstName: '',
          lastName: '',
          email: '',
          phone: '',
          coverLetter: '',
          notes: '',
          oauthUserId: '',
          characterId: ''
        });
        loadApplications();
      }
    } catch (error) {
      alert(error.message || 'Failed to create application');
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this application?')) {
      try {
        await jobsAPI.deleteApplication(id);
        loadApplications();
      } catch (error) {
        alert(error.message || 'Failed to delete application');
      }
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'pending':
        return '#ffa500';
      case 'reviewing':
        return '#4169e1';
      case 'interviewing':
        return '#9932cc';
      case 'accepted':
        return '#32cd32';
      case 'rejected':
        return '#dc143c';
      case 'archived':
        return '#666666';
      default:
        return '#666';
    }
  };

  const copyApplicationLink = (token) => {
    const url = `${window.location.origin}/application/${token}`;
    navigator.clipboard.writeText(url);
    alert('Application link copied to clipboard!');
  };

  if (loading && applications.length === 0) {
    return (
      <div className="admin-section">
        <p>Loading applications...</p>
      </div>
    );
  }
  return (
    <div className="admin-section">
      <div className="admin-section-header">
        <h2 className="admin-section-title">Job Applications</h2>
        <div className="admin-section-actions" style={{ flexWrap: 'wrap', gap: '1rem' }}>
          {canCreateApplications && (
            <button className="admin-button primary" onClick={() => setShowCreateModal(true)}>
              + Create Application
            </button>
          )}
          <select
            className="admin-form-select"
            value={selectedJob}
            onChange={(e) => setSelectedJob(e.target.value)}
            style={{ minWidth: '200px' }}
          >
            <option value="">All Positions</option>
            {jobs.map((job) => (
              <option key={job.id} value={job.id}>
                {job.title}
              </option>
            ))}
          </select>
          <select
            className="admin-form-select"
            value={selectedStatus}
            onChange={(e) => setSelectedStatus(e.target.value)}
            style={{ minWidth: '150px' }}
          >
            <option value="">All Statuses</option>
            <option value="pending">Pending</option>

            <option value="reviewing">Reviewing</option>
            <option value="interviewing">Interviewing</option>
            <option value="accepted">Accepted</option>
            <option value="rejected">Rejected</option>
            <option value="archived">Archived</option>
          </select>
        </div>
      </div>
      <div className="admin-table-wrapper">
        <table className="admin-table">
          <thead>
            <tr>
              <th>ID</th>
              <th>Position</th>
              <th>Name</th>
              <th>((Discord))</th>
              <th>Phone</th>
              <th>Status</th>
              <th>Applied</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {applications.length === 0 ? (
              <tr>
                <td colSpan="8" style={{ textAlign: 'center', padding: '2rem' }}>
                  No applications found
                </td>
              </tr>
            ) : (
              applications.map((application) => (
                <tr key={application.id}>
                  <td>{application.id}</td>
                  <td>{application.position_title}</td>
                  <td>{application.first_name} {application.last_name}</td>
                  <td>{application.email}</td>
                  <td>{application.phone || '-'}</td>
                  <td>
                    {canManageStatus ? (
                      <button
                        className="admin-button"
                        style={{
                          backgroundColor: getStatusColor(application.status),
                          color: 'white',
                          padding: '0.25rem 0.75rem',
                          fontSize: '0.85rem',
                          border: 'none',
                          cursor: 'pointer'
                        }}
                        onClick={() => handleStatusClick(application)}
                        title="Click to update status"
                      >
                        {application.status.charAt(0).toUpperCase() + application.status.slice(1)}
                      </button>
                    ) : (
                      <span
                        className="admin-status"
                        style={{ backgroundColor: getStatusColor(application.status), color: '#fff' }}
                      >
                        {application.status.charAt(0).toUpperCase() + application.status.slice(1)}
                      </span>
                    )}
                  </td>

                  <td>{new Date(application.created_at).toLocaleDateString()}</td>
                  <td>
                    <div className="admin-table-actions">
                      <button
                        className="admin-icon-button"
                        onClick={() => copyApplicationLink(application.application_token)}
                        title="Copy application link"
                      >
                        🔗
                      </button>
                      <button
                        className="admin-icon-button"
                        onClick={() => {
                          window.open(`/application/${application.application_token}`, '_blank');
                        }}
                        title="View application"
                      >
                        👁
                      </button>
                      {canDeleteApplications && (
                        <button
                          className="admin-icon-button danger"
                          onClick={() => handleDelete(application.id)}
                          title="Delete"
                        >
                          ×
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
      {showStatusModal && selectedApplication && (
        <div className="admin-modal-overlay" onClick={() => setShowStatusModal(false)}>
          <div className="admin-modal-content" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '500px' }}>
            <div className="admin-modal-header">
              <h3>Update Application Status</h3>
              <button className="admin-modal-close" onClick={() => setShowStatusModal(false)}>×</button>

            </div>
            <div className="admin-form" style={{ padding: '1.5rem' }}>
              <div className="admin-form-group">
                <label className="admin-form-label">Application</label>
                <p style={{ margin: '0.5rem 0', color: 'var(--text-primary)' }}>
                  {selectedApplication.first_name} {selectedApplication.last_name}<br />
                  <small style={{ color: 'var(--text-secondary)' }}>{selectedApplication.position_title}</small>

                  {selectedApplication.additional_roles && (() => {
                    try {
                      const roles = typeof selectedApplication.additional_roles === 'string'
                        ? JSON.parse(selectedApplication.additional_roles)
                        : selectedApplication.additional_roles;
                      if (Array.isArray(roles) && roles.length > 0) {
                        return (
                          <div style={{ marginTop: '0.5rem' }}>
                            <small style={{ color: 'var(--text-secondary)', display: 'block' }}>
                              Also interested in: {roles.map((roleId, idx) => {
                                const role = jobs.find(j => j.id === parseInt(roleId));
                                return role ? role.title : roleId;
                              }).join(', ')}
                            </small>
                          </div>
                        );
                      }
                    } catch (e) {
                      console.error('Error parsing additional roles:', e);
                    }
                    return null;
                  })()}
                </p>
              </div>
              <div className="admin-form-group">
                <label className="admin-form-label">Status</label>
                <select
                  className="admin-form-select"
                  value={statusUpdate.status}
                  onChange={(e) => setStatusUpdate({ ...statusUpdate, status: e.target.value })}
                >
                  <option value="pending">Pending</option>
                  <option value="reviewing">Reviewing</option>
                  <option value="interviewing">Interviewing</option>
                  <option value="accepted">Accepted</option>
                  <option value="rejected">Rejected</option>
                  {canArchiveApplications && <option value="archived">Archived</option>}
                </select>
              </div>
              <div className="admin-form-group">
                <label className="admin-form-label">Notes (Internal)</label>
                <textarea
                  className="admin-form-textarea"
                  value={statusUpdate.notes}
                  onChange={(e) => setStatusUpdate({ ...statusUpdate, notes: e.target.value })}
                  rows="4"
                  placeholder="Internal notes about this application..."
                />
              </div>
              <div className="admin-form-actions">
                <button
                  type="button"
                  className="admin-button secondary"
                  onClick={() => setShowStatusModal(false)}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="admin-button primary"
                  onClick={handleStatusUpdate}
                >
                  Update Status
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      {showCreateModal && (
        <div className="admin-modal-overlay" onClick={() => setShowCreateModal(false)}>
          <div className="admin-modal-content" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '700px' }}>
            <div className="admin-modal-header">
              <h3>Create Application</h3>
              <button className="admin-modal-close" onClick={() => setShowCreateModal(false)}>×</button>
            </div>
            <div className="admin-form" style={{ padding: '1.5rem' }}>
              <div className="admin-form-group">
                <label className="admin-form-label">Position *</label>
                <select
                  className="admin-form-select"
                  value={createForm.positionId}
                  onChange={(e) => setCreateForm({ ...createForm, positionId: e.target.value })}
                >
                  <option value="">Select Position</option>
                  {jobs.map((job) => (
                    <option key={job.id} value={job.id}>{job.title}</option>
                  ))}
                </select>
              </div>
              <div className="admin-form-group">
                <label className="admin-form-label">First Name *</label>

                <input
                  className="admin-form-input"
                  value={createForm.firstName}
                  onChange={(e) => setCreateForm({ ...createForm, firstName: e.target.value })}
                />

              </div>

              <div className="admin-form-group">
                <label className="admin-form-label">Last Name *</label>

                <input
                  className="admin-form-input"
                  value={createForm.lastName}
                  onChange={(e) => setCreateForm({ ...createForm, lastName: e.target.value })}
                />

              </div>

              <div className="admin-form-group">
                <label className="admin-form-label">((Discord)) *</label>

                <input
                  className="admin-form-input"
                  value={createForm.email}
                  onChange={(e) => setCreateForm({ ...createForm, email: e.target.value })}
                />

              </div>

              <div className="admin-form-group">
                <label className="admin-form-label">Phone</label>

                <input
                  className="admin-form-input"
                  value={createForm.phone}
                  onChange={(e) => setCreateForm({ ...createForm, phone: e.target.value })}
                />

              </div>

              <div className="admin-form-group">
                <label className="admin-form-label">Force OAuth User ID</label>

                <input
                  className="admin-form-input"
                  value={createForm.oauthUserId}
                  onChange={(e) => setCreateForm({ ...createForm, oauthUserId: e.target.value })}
                />

              </div>

              <div className="admin-form-group">
                <label className="admin-form-label">Force Character ID</label>

                <input
                  className="admin-form-input"
                  value={createForm.characterId}
                  onChange={(e) => setCreateForm({ ...createForm, characterId: e.target.value })}
                />

              </div>

              <div className="admin-form-group">
                <label className="admin-form-label">Cover Letter</label>

                <textarea
                  className="admin-form-textarea"
                  rows="4"
                  value={createForm.coverLetter}
                  onChange={(e) => setCreateForm({ ...createForm, coverLetter: e.target.value })}
                />

              </div>

              <div className="admin-form-group">
                <label className="admin-form-label">Internal Notes</label>

                <textarea
                  className="admin-form-textarea"
                  rows="3"
                  value={createForm.notes}
                  onChange={(e) => setCreateForm({ ...createForm, notes: e.target.value })}
                />

              </div>

              <div className="admin-form-actions">
                <button className="admin-button secondary" onClick={() => setShowCreateModal(false)}>
                  Cancel
                </button>

                <button className="admin-button primary" onClick={handleCreateApplication}>
                  Create
                </button>

              </div>

            </div>

          </div>

        </div>

      )}
    </div>

  );
};

export default ApplicationsManagement;

